import ReactInput from 'input-format/react'
import { AsYouType } from 'libphonenumber-js'
import * as React from 'react'
import type { Value } from 'react-phone-number-input'

import { nationalPhoneDigits, US_PHONE_DIGITS } from '../../utils/phone'
import { Input } from './input'

// Every number we collect is US, so the country is fixed and the +1 is implied.
const COUNTRY = 'US'

/** One typed character: a digit while there is room, ten of them, or eleven led by the country code. */
function parseDigit(character: string, value: string): string | undefined {
  if (!/\d/.test(character)) return undefined
  const room = value.startsWith('1') ? US_PHONE_DIGITS + 1 : US_PHONE_DIGITS
  return value.length < room ? character : undefined
}

/** The digits as written, with the template that lets the caret skip the punctuation. */
function formatDigits(value?: string) {
  const formatter = new AsYouType(COUNTRY)
  const text = formatter.input(value ?? '')
  return { text, template: formatter.getTemplate() }
}

/** E.164 for whatever is typed so far, a leading 1 read as the country code. */
function toValue(digits: string): Value {
  if (!digits) return '' as Value
  return `+1${digits.startsWith('1') ? digits.slice(1) : digits}` as Value
}

type FieldProps = Omit<React.ComponentProps<'input'>, 'size'>

type FormattedInputProps = Omit<FieldProps, 'onChange' | 'value'> & {
  value: string
  parse: typeof parseDigit
  format: typeof formatDigits
  inputComponent: React.ComponentType<FieldProps>
  onChange: (value?: string) => void
}

// input-format's typings intersect its onChange with the input's own; these are the props it takes.
const FormattedInput = ReactInput as unknown as React.ComponentType<FormattedInputProps>

/** The shared Input, minus its `size` variant, which an <input>'s own size attribute would collide with. */
function PhoneField(props: FieldProps) {
  return <Input {...props} />
}

type PhoneInputProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value' | 'ref' | 'size'> & {
  value?: string
  onChange?: (value: Value) => void
  /** Invalid state — reddens the control. */
  error?: boolean
}

/**
 * A US phone number, formatted as it's typed. Each keystroke reaches `onChange` in the
 * same event, so fast typing can't race a parent's echo of an earlier digit.
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, onChange, value, error, placeholder = '(555) 000-0000', ...props }, ref) => {
    const [digits, setDigits] = React.useState(() => nationalPhoneDigits(value))
    const [shownValue, setShownValue] = React.useState(value)
    const emitted = React.useRef(value)
    // A value the parent set itself (a reset, a fresh load), not the echo of a keystroke.
    if (value !== shownValue) {
      setShownValue(value)
      if (value !== emitted.current) setDigits(nationalPhoneDigits(value))
    }

    function emit(next: string) {
      const nextValue = toValue(next)
      emitted.current = nextValue
      setDigits(next)
      onChange?.(nextValue)
    }

    // Only whole-value pastes: a caret paste splices, one digit at a time, through parseDigit.
    function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
      const { value: current, selectionStart, selectionEnd } = event.currentTarget
      const replacesEverything = current === '' || (selectionStart === 0 && selectionEnd === current.length)
      if (!replacesEverything) return
      const pasted = nationalPhoneDigits(event.clipboardData.getData('text'))
      if (!pasted) return
      event.preventDefault()
      // Truncating a longer number would turn a foreign one into a valid-looking US number.
      if (pasted.length > US_PHONE_DIGITS) return
      emit(pasted)
    }

    return (
      <FormattedInput
        {...props}
        ref={ref}
        inputComponent={PhoneField}
        type="tel"
        autoComplete={props.autoComplete ?? 'tel-national'}
        className={className}
        aria-invalid={error ? true : undefined}
        placeholder={placeholder}
        value={digits}
        parse={parseDigit}
        format={formatDigits}
        onChange={(next) => emit(next ?? '')}
        onPaste={handlePaste}
      />
    )
  }
)
PhoneInput.displayName = 'PhoneInput'
